/**
 * ChatDockContext — the seam anything in the app uses to open the Clubhouse.
 *
 * It exists so the presence rail can hand the dock a draft ("@donald ") without
 * the two components knowing about each other, and it is deliberately kept in
 * its OWN module with no heavy imports: {@link ChatDock} lazy-loads the emoji
 * picker, and a consumer importing the hook from the dock's module would pull
 * that graph into the main bundle.
 *
 * `seed` carries a NONCE rather than just text because clicking the same person
 * twice must re-open the dock both times — a bare string would compare equal and
 * the second click would do nothing.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export interface ChatDraftSeed {
  text: string;
  /** Bumped on every open so an identical text still registers as a new event. */
  nonce: number;
}

export interface ChatDockApi {
  /** Whether the dock is expanded (false = minimized to its bubble). */
  open: boolean;
  /** The most recent draft handed in by `openChat`, or null. */
  seed: ChatDraftSeed | null;
  /** Expand the dock, optionally seeding the composer's draft. */
  openChat: (text?: string) => void;
  /** Collapse to the bubble. */
  minimizeChat: () => void;
}

const ChatDockContext = createContext<ChatDockApi | null>(null);

export function ChatDockProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [seed, setSeed] = useState<ChatDraftSeed | null>(null);

  const openChat = useCallback((text?: string) => {
    setOpen(true);
    if (text !== undefined) setSeed((prev) => ({ text, nonce: (prev?.nonce ?? 0) + 1 }));
  }, []);

  const minimizeChat = useCallback(() => setOpen(false), []);

  const value = useMemo<ChatDockApi>(
    () => ({ open, seed, openChat, minimizeChat }),
    [open, seed, openChat, minimizeChat],
  );

  return <ChatDockContext.Provider value={value}>{children}</ChatDockContext.Provider>;
}

/**
 * The dock's controls. Returns null OUTSIDE a provider rather than throwing:
 * consumers are decoration (the presence rail's "say hi" affordance), and a
 * component test that renders the rail alone must not be forced to wrap it.
 */
export function useChatDock(): ChatDockApi | null {
  return useContext(ChatDockContext);
}
